import React from 'react'
import { EMkStepStatus, withStepDefaults } from '@typescript'
import { ACCOUNT_LOCKOUT, API, SIGNIN_AUTOCOMPLETE, SIGNIN_LABELS, SIGNIN_PLACEHOLDERS, SIGNIN_STEP_KEYS, VALIDATION_MESSAGES } from '@constants'
import { serverUrl } from '@utils/MkEnv'
import { DataStore } from '@utils/DataStore'
import { AccountLockoutModal } from '@ui/AccountLockoutModal'
import { getLockoutVariant } from '@features/authentication/utils'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { useAuthLabelsContext } from '@features/account/contexts/AuthLabelsContext'
import type { FooterLinks, SignInFormData, SignInFormField, SignInStep, SignInStepName, SignInStepRegistry } from './types'
import { fieldsForStep } from './validationConfig'
import { validateField } from './fieldValidators'
import { createField, signinStepFields } from './fieldDefinitions'

type SignInStepInput = Omit<SignInStep, 'fieldSet' | 'formState'> & Partial<Pick<SignInStep, 'fieldSet' | 'formState'>>

const createSignInStep = (step: SignInStepInput): SignInStep => withStepDefaults(step) as SignInStep

// Helper function to create AccountLockoutModal component with appropriate configuration
const createAccountLockedComponent = () => {
  const AccountLockoutStep: React.FC = () => {
    const authState = useAuthentication()
    const { getLabel } = useAuthLabelsContext()
    const lockoutVariant = getLockoutVariant(authState)

    const handleCallUs = () => {
      window.location.href = 'tel:1-800-765-4321'
    }

    const body =
      lockoutVariant === 'MFA_CRITICAL'
        ? getLabel('lockout', 'lockout', 'mfaCritical.body', ACCOUNT_LOCKOUT.MFA_CRITICAL_BODY)
        : getLabel('signIn', 'accountLocked', 'description', ACCOUNT_LOCKOUT.PASSWORD_BODY)
    const buttonLabel =
      lockoutVariant === 'MFA_CRITICAL'
        ? getLabel('lockout', 'lockout', 'mfaCritical.button', ACCOUNT_LOCKOUT.MFA_CRITICAL_BUTTON)
        : getLabel('lockout', 'lockout', 'actions.callUs', ACCOUNT_LOCKOUT.CALL_US_BUTTON)

    return React.createElement(AccountLockoutModal, {
      body,
      buttonLabel,
      buttonAction: handleCallUs,
      buttonVariant: 'primary',
    })
  }

  return AccountLockoutStep
}

/**
 * Type-safe interfaces for dynamic content
 */
interface FieldDefinition {
  id: string
  name: string
  label: string
  type: string
  required: boolean
  placeholder?: string
  autoComplete?: string
  autoFocus?: boolean
  value?: string | boolean
  options?: Array<{ label: string; value: string }>
}

interface ForgotUsernamePayload {
  membershipNumber: string
  firstName?: string
  lastName?: string
  companyName?: string
  phoneNumber?: string
  contractNumber?: string
  suffix?: string
}

interface FieldDefinitions {
  [key: string]: FieldDefinition
}

interface DynamicContent {
  fieldDefinitions?: FieldDefinitions
  footerLinks?: FooterLinks
  tabs?: Record<string, string>
  buttons?: Record<string, string>
  fields?: Record<string, string>
  placeholders?: Record<string, string>
  errors?: Record<string, string>
  messages?: Record<string, string>
  recoverUsername?: Record<string, string>
  resetPassword?: Record<string, string>
  [key: string]: unknown
}

const collectStepErrors = (formData: SignInFormData, stepName: SignInStepName): string[] => {
  const errors: string[] = []
  const fields = fieldsForStep[stepName] || []

  fields.forEach((name) => {
    const field = formData[name]
    if (field && typeof field === 'object' && 'value' in field) {
      const { value } = field
      const res = validateField(name, value, formData)
      if (!res.isValid) {
        errors.push(res.message)
      }
    }
  })

  return errors
}

// Create Map-based step registry for semantic step access
export const SIGNIN_STEPS: SignInStepRegistry = new Map<SignInStepName, SignInStep>([
  [
    SIGNIN_STEP_KEYS.SIGNIN,
    createSignInStep({
      key: SIGNIN_STEP_KEYS.SIGNIN,
      name: SIGNIN_STEP_KEYS.SIGNIN,
      title: SIGNIN_LABELS.SIGN_IN_TITLE,
      status: EMkStepStatus.ACTIVE,
      conditionals: {},
      content: {
        title: SIGNIN_LABELS.SIGN_IN,
      },
      dynamicContent: {
        footerLinks: {
          needHelp: SIGNIN_LABELS.NEED_HELP_SIGNING_IN,
          register: SIGNIN_LABELS.REGISTER,
        },
        fieldDefinitions: signinStepFields,
      },
      props: {
        errorHandling: {
          validationErrors: 'block', // Show validation errors in main banner (blocks progression)
          apiErrors: 'block', // Show API errors in main banner (blocks progression)
          preventProgression: true, // Block progression until validation passes
        },
      },
      isVisible: () => true,
      validation: (formData) => collectStepErrors(formData, 'signin').length === 0,
      loader: () => import('./steps/_MkSignInStep1').then((m) => ({ default: m.MkSignInStep1 })),
    }),
  ],
  [
    SIGNIN_STEP_KEYS.HELP_PANEL,
    createSignInStep({
      key: SIGNIN_STEP_KEYS.HELP_PANEL,
      name: SIGNIN_STEP_KEYS.HELP_PANEL,
      title: SIGNIN_LABELS.HELP_RECOVERY_TITLE,
      status: EMkStepStatus.ACTIVE,
      conditionals: {},
      content: {
        title: SIGNIN_LABELS.HELP_RECOVERY_TITLE,
        // description: SIGNIN_LABELS.HELP_DESCRIPTION,
      },
      dynamicContent: {
        tabs: {
          forgotUsername: SIGNIN_LABELS.FORGOT_USERNAME_TAB,
          forgotPassword: SIGNIN_LABELS.FORGOT_PASSWORD_TAB,
        },
        recoverUsername: {
          title: SIGNIN_LABELS.RECOVER_USERNAME,
          description: SIGNIN_LABELS.RECOVER_USERNAME_DESCRIPTION,
        },
        resetPassword: {
          title: SIGNIN_LABELS.RESET_PASSWORD,
          description: SIGNIN_LABELS.RESET_PASSWORD_DESCRIPTION,
          subtitleDescription: SIGNIN_LABELS.RESET_PASSWORD_SUBTITLE,
          warningMessage: SIGNIN_LABELS.RESET_PASSWORD_WARNING,
          defaultSubtitleDescription: SIGNIN_LABELS.FORGOT_PASSWORD_DEFAULT_SUBTITLE,
          defaultDescription: SIGNIN_LABELS.FORGOT_PASSWORD_DEFAULT_DESCRIPTION,
        },
        buttons: {
          backButton: SIGNIN_LABELS.BACK_TO_SIGN_IN,
          continueButton: SIGNIN_LABELS.CONTINUE,
          loadingText: SIGNIN_LABELS.LOADING,
          sendResetLink: SIGNIN_LABELS.SEND_RESET_LINK,
          sendEmailButton: SIGNIN_LABELS.SEND_EMAIL_BUTTON,
          recoverUsernameButton: SIGNIN_LABELS.RECOVER_USERNAME_BUTTON,
          sending: SIGNIN_LABELS.SENDING,
          emailSent: SIGNIN_LABELS.EMAIL_SENT,
        },
        fields: {
          emailAddress: SIGNIN_LABELS.EMAIL_ADDRESS,
          verificationMethod: SIGNIN_LABELS.VERIFICATION_METHOD,
          registerAsCompany: SIGNIN_LABELS.REGISTER_AS_COMPANY,
          username: SIGNIN_LABELS.USERNAME,
        },
        fieldDefinitions: {
          email: createField({
            id: 'email',
            name: 'email',
            label: SIGNIN_LABELS.EMAIL_ADDRESS,
            type: 'email',
            required: true,
            placeholder: SIGNIN_PLACEHOLDERS.EMAIL,
            autoComplete: 'email',
          }),
          username: createField({
            id: 'username',
            name: 'username',
            label: SIGNIN_LABELS.USERNAME,
            type: 'text',
            required: true,
            placeholder: SIGNIN_PLACEHOLDERS.USERNAME,
            autoComplete: 'username',
          }),
          verificationMethod: createField({
            id: 'verificationMethod',
            name: 'verificationMethod',
            label: SIGNIN_LABELS.VERIFICATION_METHOD,
            type: 'select',
            required: true,
            value: 'email',
            options: [
              { label: SIGNIN_LABELS.EMAIL_LABEL, value: 'email' },
              { label: SIGNIN_LABELS.PHONE_LABEL, value: 'phone' },
            ],
          }),
          registerAsCompany: createField({
            id: 'registerAsCompany',
            name: 'registerAsCompany',
            label: SIGNIN_LABELS.REGISTER_AS_COMPANY,
            type: 'checkbox',
            required: false,
            value: false,
          }),
          ownerNumber: createField({
            id: 'ownerNumber',
            name: 'ownerNumber',
            label: SIGNIN_LABELS.OWNER_NUMBER,
            type: 'text',
            required: true,
            placeholder: SIGNIN_PLACEHOLDERS.OWNER_NUMBER,
            autoComplete: 'off',
          }),
          companyName: createField({
            id: 'companyName',
            name: 'companyName',
            label: SIGNIN_LABELS.COMPANY_NAME_LABEL,
            type: 'text',
            required: false,
            placeholder: SIGNIN_PLACEHOLDERS.COMPANY_NAME,
            autoComplete: SIGNIN_AUTOCOMPLETE.ORGANIZATION,
          }),
          firstName: createField({
            id: 'firstName',
            name: 'firstName',
            label: SIGNIN_LABELS.FIRST_NAME_LABEL,
            type: 'text',
            required: true,
            placeholder: SIGNIN_PLACEHOLDERS.FIRST_NAME,
            autoComplete: SIGNIN_AUTOCOMPLETE.GIVEN_NAME,
          }),
          lastName: createField({
            id: 'lastName',
            name: 'lastName',
            label: SIGNIN_LABELS.LAST_NAME_LABEL,
            type: 'text',
            required: true,
            placeholder: SIGNIN_PLACEHOLDERS.LAST_NAME,
            autoComplete: SIGNIN_AUTOCOMPLETE.FAMILY_NAME,
          }),
          phone: createField({
            id: 'phone',
            name: 'phone',
            label: SIGNIN_LABELS.PHONE_NUMBER,
            type: 'tel',
            required: false,
            placeholder: SIGNIN_PLACEHOLDERS.PHONE,
            autoComplete: SIGNIN_AUTOCOMPLETE.TEL,
          }),
          phoneCountry: createField({
            id: 'phoneCountry',
            name: 'phoneCountry',
            label: SIGNIN_LABELS.PHONE_COUNTRY_LABEL,
            type: 'text',
            required: false,
            value: SIGNIN_LABELS.DEFAULT_PHONE_COUNTRY,
          }),
          contractNumber: createField({
            id: 'contractNumber',
            name: 'contractNumber',
            label: SIGNIN_LABELS.CONTRACT_NUMBER_LABEL,
            type: 'text',
            required: false,
            placeholder: SIGNIN_PLACEHOLDERS.CONTRACT_NUMBER,
            autoComplete: 'off',
          }),
          // Additional fields for recovery operations
          tempCode: createField({
            id: 'tempCode',
            name: 'tempCode',
            label: SIGNIN_LABELS.TEMPORARY_CODE,
            type: 'text',
            required: true,
            placeholder: SIGNIN_LABELS.ENTER_CODE_SENT_TO_EMAIL,
            autoComplete: 'off',
          }),
          securityQuestion1: createField({
            id: 'securityQuestion1',
            name: 'securityQuestion1',
            label: SIGNIN_LABELS.SECURITY_QUESTION_1,
            type: 'text',
            required: true,
            placeholder: SIGNIN_LABELS.ENTER_SECURITY_QUESTION,
            autoComplete: 'off',
          }),
          securityAnswer1: createField({
            id: 'securityAnswer1',
            name: 'securityAnswer1',
            label: SIGNIN_LABELS.ANSWER_1,
            type: 'text',
            required: true,
            placeholder: SIGNIN_LABELS.ENTER_ANSWER,
            autoComplete: 'off',
          }),
          securityQuestion2: createField({
            id: 'securityQuestion2',
            name: 'securityQuestion2',
            label: SIGNIN_LABELS.SECURITY_QUESTION_2,
            type: 'text',
            required: true,
            placeholder: SIGNIN_LABELS.ENTER_SECURITY_QUESTION,
            autoComplete: 'off',
          }),
          securityAnswer2: createField({
            id: 'securityAnswer2',
            name: 'securityAnswer2',
            label: SIGNIN_LABELS.ANSWER_2,
            type: 'text',
            required: true,
            placeholder: SIGNIN_LABELS.ENTER_ANSWER,
            autoComplete: 'off',
          }),
          newPassword: createField({
            id: 'newPassword',
            name: 'newPassword',
            label: SIGNIN_LABELS.NEW_PASSWORD_LABEL,
            type: 'password',
            required: true,
            placeholder: SIGNIN_LABELS.ENTER_NEW_PASSWORD,
            autoComplete: 'new-password',
          }),
          confirmPassword: createField({
            id: 'confirmPassword',
            name: 'confirmPassword',
            label: SIGNIN_LABELS.CONFIRM_PASSWORD_LABEL,
            type: 'password',
            required: true,
            placeholder: SIGNIN_LABELS.CONFIRM_NEW_PASSWORD,
            autoComplete: 'new-password',
          }),
        },
        placeholders: {
          enterEmail: SIGNIN_LABELS.ENTER_EMAIL_PLACEHOLDER,
        },
        errors: {
          errorOccurred: SIGNIN_LABELS.ERROR_OCCURRED,
          emailNotFound: SIGNIN_LABELS.EMAIL_NOT_FOUND,
          accountLocked: SIGNIN_LABELS.ACCOUNT_LOCKED,
          tooManyAttempts: SIGNIN_LABELS.TOO_MANY_ATTEMPTS,
        },
      },
      props: {
        errorHandling: {
          validationErrors: 'inline', // Show validation errors inline within the step
          apiErrors: 'block', // Show API errors in main banner (blocks progression)
          preventProgression: true, // Block progression until validation passes
        },
        // API configuration for recovery operations
        apiConfig: {
          forgotUsername: {
            method: 'POST',
            endpoint: `${serverUrl}${API}/username`,
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            // todo: clean up this type adjustment
            body: (formData: { [key: string]: any }) => {
              // const registerAsCompany = Boolean((formData.registerAsCompany as SignInFormField)?.value)
              // const method = (formData.verificationMethod as SignInFormField)?.value as 'phone' | 'contract'

              const payload: Partial<ForgotUsernamePayload> = {
                membershipNumber: formData.membershipNumber ? formData.membershipNumber : null,
                companyName: formData.companyName ? formData.companyName : null,
                firstName: formData.firstName ? formData.firstName : null,
                lastName: formData.lastName ? formData.lastName : null,
                suffix: formData.suffix ? formData.suffix : null,
                phoneNumber: formData.phone?.phoneNumber ? formData.phone.phoneNumber : null,
                contractNumber: formData.contractNumber ? formData.contractNumber : null,
              }

              // Remove undefined or empty optional fields to avoid sending empty strings
              Object.keys(payload).forEach((k) => {
                const key = k as keyof ForgotUsernamePayload
                if (payload[key] === undefined || payload[key] === null || (typeof payload[key] === 'string' && payload[key]?.trim() === '')) {
                  delete payload[key]
                }
              })

              return payload
            },
          },
          forgotPassword: {
            method: 'POST',
            endpoint: () => `${serverUrl}${API}/okta/authn/recovery/password`,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: (formData: SignInFormData) => {
              const username = String((formData.userName as SignInFormField)?.value || (formData.email as SignInFormField)?.value || '')
              return {
                username,
                factorType: 'EMAIL',
              }
            },
          },
        },
      },
      isVisible: () => true,
      validation: () => true, // Help panel doesn't require validation
      loader: () => import('./steps/_HelpPanelStep').then((m) => ({ default: m.default })),
    }),
  ],
  [
    SIGNIN_STEP_KEYS.FORGOT_USERNAME_CONFIRMATION,
    createSignInStep({
      key: SIGNIN_STEP_KEYS.FORGOT_USERNAME_CONFIRMATION,
      name: SIGNIN_STEP_KEYS.FORGOT_USERNAME_CONFIRMATION,
      title: SIGNIN_LABELS.USERNAME_RECOVERED,
      status: EMkStepStatus.ACTIVE,
      conditionals: {},
      content: {
        title: SIGNIN_LABELS.USERNAME_RECOVERED,
      },
      dynamicContent: {
        buttons: {
          signInWithUsername: SIGNIN_LABELS.SIGN_IN_WITH_USERNAME,
        },
      },
      props: {
        errorHandling: {
          validationErrors: 'none', // No validation errors for this step
          apiErrors: 'block', // Show API errors in main banner
          preventProgression: false, // Allow progression (confirmation step)
        },
      },
      isVisible: () => true,
      validation: () => true, // Confirmation step doesn't require validation
      loader: () => import('./steps/_UsernameRecoveredStep').then((m) => ({ default: m.UsernameRecoveredStep })),
    }),
  ],
  [
    SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_LINK,
    createSignInStep({
      key: SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_LINK,
      name: SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_LINK,
      title: SIGNIN_LABELS.RECOVERY_EMAIL_SENT,
      status: EMkStepStatus.ACTIVE,
      conditionals: {},
      content: {
        title: SIGNIN_LABELS.RECOVERY_EMAIL_SENT,
      },
      dynamicContent: {
        buttons: {
          emailChecked: SIGNIN_LABELS.EMAIL_CHECKED,
          resendEmail: SIGNIN_LABELS.RESEND_EMAIL,
          backToRecovery: SIGNIN_LABELS.BACK_TO_RECOVERY_OPTIONS,
        },
        messages: {
          resendWarning: SIGNIN_LABELS.RESEND_WARNING,
        },
      },
      props: {
        errorHandling: {
          validationErrors: 'none', // No validation errors for this step
          apiErrors: 'block', // Show API errors in main banner
          preventProgression: false, // Allow progression (email sent step)
        },
      },
      isVisible: () => true,
      validation: () => true, // Email sent step doesn't require validation
      loader: () => import('./steps/_PasswordRecoveryEmailSentStep').then((m) => ({ default: m.PasswordRecoveryEmailSentStep })),
    }),
  ],
  [
    SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM,
    createSignInStep({
      key: SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM,
      name: SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM,
      title: SIGNIN_LABELS.SIGN_IN_RESET_PASSWORD,
      status: EMkStepStatus.ACTIVE,
      conditionals: {},
      content: {
        title: SIGNIN_LABELS.SET_NEW_PASSWORD,
      },
      dynamicContent: {
        buttons: {
          setPassword: SIGNIN_LABELS.SET_PASSWORD_BUTTON,
        },
        fields: {
          newPassword: SIGNIN_LABELS.NEW_PASSWORD_LABEL,
          confirmPassword: SIGNIN_LABELS.CONFIRM_PASSWORD_LABEL,
        },
        fieldDefinitions: {
          newPassword: createField({
            id: 'newPassword',
            name: 'newPassword',
            label: SIGNIN_LABELS.NEW_PASSWORD_LABEL,
            type: 'password',
            required: true,
            placeholder: SIGNIN_PLACEHOLDERS.NEW_PASSWORD,
            autoComplete: SIGNIN_AUTOCOMPLETE.NEW_PASSWORD,
          }),
          confirmPassword: createField({
            id: 'confirmPassword',
            name: 'confirmPassword',
            label: SIGNIN_LABELS.CONFIRM_PASSWORD_LABEL,
            type: 'password',
            required: true,
            placeholder: SIGNIN_PLACEHOLDERS.CONFIRM_PASSWORD,
            autoComplete: SIGNIN_AUTOCOMPLETE.NEW_PASSWORD,
          }),
        },
        placeholders: {
          enterNewPassword: SIGNIN_PLACEHOLDERS.NEW_PASSWORD,
          confirmNewPassword: SIGNIN_PLACEHOLDERS.CONFIRM_PASSWORD,
        },
      },
      props: {
        errorHandling: {
          validationErrors: 'block', // Show validation errors in main banner
          apiErrors: 'block', // Show API errors in main banner
          preventProgression: true, // Block progression until validation passes
        },
        apiConfig: {
          method: 'POST',
          endpoint: () => {
            // Try to get the dynamic endpoint from DataStore first
            const dynamicEndpoint = DataStore.getItem('resetPasswordUrl')
            // Only use the dynamic endpoint if it is NOT an absolute URL (e.g., Okta link)
            // This ensures we route through mkback proxy even if Okta returned a full href
            if (dynamicEndpoint && !/^https?:\/\//i.test(dynamicEndpoint)) {
              return dynamicEndpoint
            }
            // Route through mkback Okta proxy (preferred and safe default)
            return `${serverUrl}${API}/okta/authn/credentials/reset_password`
          },
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: (formData: SignInFormData) => {
            const stateToken = DataStore.getItem('stateToken')

            if (!stateToken) {
              throw new Error(VALIDATION_MESSAGES.NO_AUTH_TOKEN)
            }

            const passwordValue = (formData.password as SignInFormField)?.value || (formData.newPassword as SignInFormField)?.value

            if (!passwordValue) {
              throw new Error(VALIDATION_MESSAGES.PASSWORD_REQUIRED)
            }

            return {
              stateToken,
              newPassword: passwordValue,
            }
          },
        },
      },
      isVisible: () => true,
      validation: (formData) => collectStepErrors(formData, 'forgot-password-reset-form').length === 0,
      loader: () => import('./steps/_ResetPasswordStep').then((m) => ({ default: m.ResetPasswordStep })),
    }),
  ],
  [
    SIGNIN_STEP_KEYS.ACCOUNT_LOCKED,
    createSignInStep({
      key: SIGNIN_STEP_KEYS.ACCOUNT_LOCKED,
      name: SIGNIN_STEP_KEYS.ACCOUNT_LOCKED,
      title: SIGNIN_LABELS.ACCOUNT_LOCKED_TITLE,
      status: EMkStepStatus.ACTIVE,
      conditionals: {},
      content: {
        description: SIGNIN_LABELS.ACCOUNT_LOCKED_CAPTION,
        title: SIGNIN_LABELS.ACCOUNT_LOCKED_TITLE,
        icon: ACCOUNT_LOCKOUT.ICON_NAME,
      },
      props: {
        headerIcon: 'lock-keyhole',
        headerIconClassName: 'form-header__icon--large',
        headerIconSet: 'fal',
        errorHandling: {
          validationErrors: 'none', // No validation errors for this step
          apiErrors: 'none', // No API errors for this step
          preventProgression: true, // Block progression (locked state)
        },
      },
      isVisible: () => true,
      validation: () => true, // Locked step doesn't require validation
      loader: () => Promise.resolve({ default: createAccountLockedComponent() }),
    }),
  ],
])

// Helper function to get step order array
export const getStepOrder = (): SignInStepName[] => Array.from(SIGNIN_STEPS.keys())

// Helper functions for step management - Map-based access
export const getCurrentStep = (stepName: SignInStepName): SignInStep | undefined => SIGNIN_STEPS.get(stepName)

export const getTotalSteps = (): number => SIGNIN_STEPS.size

export const canGoToStep = (currentStepName: SignInStepName, targetStepName: SignInStepName): boolean => {
  const step = SIGNIN_STEPS.get(targetStepName)
  return step?.isVisible?.(targetStepName) ?? false
}

/**
 * Validate a single step and return whether it can proceed.
 * Respects the step's error handling configuration.
 */
export const validateCurrentStep = (stepName: SignInStepName, formData: SignInFormData): boolean => {
  const step = SIGNIN_STEPS.get(stepName)
  if (!step) return false

  // If step is configured to allow progression despite errors, return true
  const errorHandling = step.props?.errorHandling as { preventProgression?: boolean } | undefined
  if (errorHandling?.preventProgression === false) {
    return true
  }

  // Otherwise, validate normally
  const errors = collectStepErrors(formData, step.name)
  return errors.length === 0
}

export const getStepValidationErrors = (stepName: SignInStepName, formData: SignInFormData): string[] => {
  const step = getCurrentStep(stepName)
  if (!step) return [SIGNIN_LABELS.STEP_NOT_FOUND]
  return collectStepErrors(formData, step.name)
}

export const canProceedToNextStep = (currentStepName: SignInStepName, formData: SignInFormData): boolean => validateCurrentStep(currentStepName, formData)

const getStepContentValue = (value: string | string[] | undefined): string => (Array.isArray(value) ? value.join(' ') : value || '')

// Helper functions to access step content - using step names
export const getStepContent = (stepName: SignInStepName, contentKey: string): string => {
  const step = getCurrentStep(stepName)
  return getStepContentValue(step?.content?.[contentKey])
}

export const getStepField = (stepName: SignInStepName, fieldKey: string) => {
  const step = getCurrentStep(stepName)
  const fieldDefinitions = step?.dynamicContent?.fieldDefinitions as FieldDefinitions | undefined
  return fieldDefinitions?.[fieldKey]
}

export const getStepFooterLinks = (stepName: SignInStepName) => {
  const step = getCurrentStep(stepName)
  const footerLinks = step?.dynamicContent?.footerLinks as FooterLinks | undefined
  return footerLinks || {}
}

export const getStepDynamicContent = (stepName: SignInStepName, contentKey: string) => {
  const step = getCurrentStep(stepName)
  const dynamicContent = step?.dynamicContent as DynamicContent | undefined
  return dynamicContent?.[contentKey]
}

// Helper functions to access step titles and descriptions - using step names
export const getStepTitle = (stepName: SignInStepName): string => {
  const step = getCurrentStep(stepName)
  return step?.title || SIGNIN_LABELS.HELP_RECOVERY_TITLE
}

export const getStepCaption = (stepName: SignInStepName): string => {
  const step = getCurrentStep(stepName)
  return getStepContentValue(step?.content?.caption)
}

/**
 * Create initial form data from field definitions in the registry
 */
export const createFormDataFromRegistry = (stepName: SignInStepName, overrideValues?: ReturnType<typeof getStepDynamicContent>) => {
  const fieldDefinitions = getStepDynamicContent(stepName, 'fieldDefinitions') as FieldDefinitions | undefined

  if (!fieldDefinitions) {
    return {}
  }

  const formData: Record<string, SignInFormField> = {}

  Object.entries(fieldDefinitions).forEach(([fieldName, fieldDef]) => {
    const overrideDef = (overrideValues as Record<string, Partial<FieldDefinition>> | undefined)?.[fieldName]
    const effectiveFieldDef: FieldDefinition = { ...fieldDef, ...(overrideDef || {}) }

    // Handle different field types appropriately
    let defaultValue: string | boolean = ''
    if (effectiveFieldDef.type === 'checkbox') {
      defaultValue = typeof effectiveFieldDef.value === 'boolean' ? effectiveFieldDef.value : false
    } else {
      defaultValue = typeof effectiveFieldDef.value === 'string' ? effectiveFieldDef.value : ''
    }

    const isRequired = effectiveFieldDef.required || false

    formData[fieldName] = {
      value: defaultValue,
      isValid: true, // Start all fields as valid, validate on interaction (matching createSignInField)
      required: isRequired,
      type: effectiveFieldDef.type || 'text',
      id: effectiveFieldDef.id || fieldName,
      name: effectiveFieldDef.name || fieldName,
      label: effectiveFieldDef.label || fieldName,
      validationMessage: '',
      errorMessage: '',
      placeholder: effectiveFieldDef.placeholder || '',
      autoComplete: effectiveFieldDef.autoComplete || '',
    }
  })

  return formData
}
